import { forwardRef, useEffect, useImperativeHandle, useRef, type ForwardedRef, type ReactElement, type ReactNode, type UIEvent } from 'react';
import { Link } from 'react-router-dom';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useNavigate } from '@/lib/legacy';
import { dismissNotice, notifyError } from '@/lib/notify';

/** A list column with an explicit width: the table's layout is fixed, so it never resizes as pages load (D36). */
export type ListColumn<T> = ColumnDef<T> & {
  /** The column's width in px. When the table is wider than its columns, they share the extra room in proportion. */
  size: number;
};

export type ListTableProps<T> = {
  rows: T[];
  columns: ListColumn<T>[];
  /** The page a row opens: the insured link's target, and where a click anywhere on the row goes. */
  rowPath: (row: T) => string;
  rowId: (row: T) => string;
  /** The scroll region's accessible name: the list's label, e.g. "Policies". */
  label: string;
  emptyMessage: string;
  loading: boolean;
  /** Any request in flight, a cached refresh included; the next page waits for it (D29). */
  fetching?: boolean;
  /** The last request failed. */
  failed?: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
};

/** The React Native-style scroll command the search context sends on a search change. */
export type ListTableHandle = { scrollTo: (to: { x?: number; y?: number; animated?: boolean }) => void };

const LOAD_MORE_WITHIN = 200;
/** The most pages the table asks for on its own in a row, with no scroll by the user in between. */
const MAX_FILLS = 3;

const scrollable = (element: HTMLElement) => /auto|scroll/.test(getComputedStyle(element).overflowY);

/** A cell's full value, for its hover text; nothing when it has none. */
const hoverText = (value: unknown) => ((typeof value === 'string' || typeof value === 'number') && value !== '' ? String(value) : undefined);

/**
 * A list page's table (Policies, Quotes, Applications): its own scroller with a sticky header,
 * loading the next page as the user nears the end (D15, D16) and while the rows don't fill it
 * (D29). Clicking a row opens it; the insured cell is the row's link (`insuredColumn`). The layout
 * is fixed: each column has its width, and a cell truncates at it, its full value on hover (D36).
 */
function ListTableInner<T>(
  { rows, columns, rowPath, rowId, label, emptyMessage, loading, fetching = loading, failed = false, hasMore, onLoadMore }: ListTableProps<T>,
  ref: ForwardedRef<ListTableHandle>
) {
  const navigate = useNavigate();
  const scroller = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      scrollTo: ({ x = 0, y = 0 }) => scroller.current?.scrollTo?.({ left: x, top: y }),
    }),
    []
  );

  const table = useReactTable({ data: rows, columns, getCoreRowModel: getCoreRowModel(), getRowId: rowId });

  // D29: rows that don't fill the list can't be scrolled, so after each page renders, ask for the
  // next one while the content still fits (a box with no height, a hidden list, never asks). The
  // ask waits a tick: useAsyncSearch settles its own paging state in its effects for the same
  // render, which run after this one and would drop an ask made now.
  // D16's stop rule: a next page that comes back empty leaves the hook's `hasMore` true and hands
  // back the same rows, so the table remembers which rows it last asked after and never asks again
  // for those. A new page, or a new search, is a new array: when a late first-page request makes
  // useAsync discard the answer to an ask, its answer is a new array and the table asks again.
  // A request that starts between the effect and the tick is caught by reading `fetching` again
  // when the tick fires.
  // It fails safe: it asks only while the region can scroll, and for at most MAX_FILLS pages in a
  // row until the user scrolls or a new search starts, so a region whose height is never bounded
  // can't fetch the whole list. A failed page also leaves the same rows; it is asked for once more.
  // D37: a failed page the table won't ask for again on its own (the retry failed too, or the list
  // can't fill) raises an error toast whose "Try again" asks for it again.
  const askedAfter = useRef<T[] | null>(null);
  const retriedAfter = useRef<T[] | null>(null);
  const fills = useRef(0);
  const firstRow = useRef<T | undefined>(undefined);
  if (rows[0] !== firstRow.current) {
    // A new search (or the first page again): its own count of pages asked for.
    firstRow.current = rows[0];
    fills.current = 0;
  }
  const fetchingNow = useRef(fetching);
  fetchingNow.current = fetching;
  const rowsNow = useRef(rows);
  rowsNow.current = rows;
  const onLoadMoreNow = useRef(onLoadMore);
  onLoadMoreNow.current = onLoadMore;

  // An ask for the page after `rows`. Only an ask made while nothing is in flight is remembered:
  // one made during a request is dropped by useAsyncSearch, so it can't mark the rows as asked after.
  const askAfter = (after: T[]) => {
    if (!fetchingNow.current) askedAfter.current = after;
    onLoadMoreNow.current();
  };

  // The toast is about the page after the rows it was raised for: it goes once that page is asked
  // for again, or other rows (a later page, a new search) replace them.
  // Each raise is a new toast: sonner keeps a dismissed toast's id while it animates out, and a
  // toast raised again under that id would stay hidden.
  const toast = useRef<{ id: string | number; after: T[] } | null>(null);
  const takeDownToast = () => {
    if (toast.current) dismissNotice(toast.current.id);
    toast.current = null;
  };
  const tellFailed = () => {
    if (toast.current?.after === rows) return;
    takeDownToast();
    const id = notifyError(`Couldn't load more ${label.toLowerCase()}`, {
      // Pressing it closes the toast.
      action: {
        label: 'Try again',
        onClick: () => {
          if (toast.current?.id === id) toast.current = null;
          askAfter(rowsNow.current);
        },
      },
      onDismiss: () => {
        if (toast.current?.id === id) toast.current = null;
      },
    });
    toast.current = { id, after: rows };
  };
  useEffect(() => {
    if (toast.current && (!failed || toast.current.after !== rows)) takeDownToast();
  }, [rows, failed]);
  useEffect(() => takeDownToast, []);

  useEffect(() => {
    if (fetching || !hasMore) return undefined;
    const retrying = askedAfter.current === rows;
    // The page after these rows came back empty: the list has ended (D16).
    if (retrying && !failed) return undefined;
    if (retrying && retriedAfter.current === rows) {
      tellFailed();
      return undefined;
    }
    const timer = setTimeout(() => {
      if (fetchingNow.current) return;
      const element = scroller.current;
      if (fills.current < MAX_FILLS && element && scrollable(element) && element.clientHeight > 0 && element.scrollHeight <= element.clientHeight) {
        if (retrying) retriedAfter.current = rows;
        fills.current += 1;
        askAfter(rows);
      } else if (retrying) {
        tellFailed();
      }
    }, 0);
    return () => clearTimeout(timer);
    // tellFailed and askAfter read only refs and props listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, fetching, failed, hasMore, onLoadMore, label]);

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    fills.current = 0;
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (!hasMore || scrollTop + clientHeight < scrollHeight - LOAD_MORE_WITHIN) return;
    // The page after these rows came back empty: the list has ended (D16). A failed one is asked
    // for again.
    if (askedAfter.current === rows && !failed) return;
    askAfter(rows);
  };

  return (
    <div
      ref={scroller}
      role="region"
      aria-label={label}
      onScroll={onScroll}
      // Inline, so the fill rule's check that the region can scroll reads it wherever it renders.
      // The gutter keeps room for a classic scrollbar from the start, so the columns don't narrow
      // when the rows first overflow (D36).
      style={{ overflowX: 'auto', overflowY: 'auto', scrollbarGutter: 'stable' }}
      className="min-h-0 flex-1 rounded-lg border [&>[data-slot=table-container]]:overflow-visible"
    >
      <Table className="table-fixed">
        <colgroup>
          {columns.map((column, index) => (
            <col key={column.id ?? index} style={{ width: `${column.size}px` }} />
          ))}
        </colgroup>
        <TableHeader className="sticky top-0 z-10 bg-background">
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => (
                <TableHead
                  key={header.id}
                  title={hoverText(header.column.columnDef.header)}
                  className="truncate text-[13px] font-bold text-text-subdued"
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} className="cursor-pointer" onClick={() => navigate.to(rowPath(row.original))}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} title={hoverText(cell.getValue())} className="truncate text-sm text-text-subdued">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center text-lg text-muted-foreground">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      {loading ? (
        <div className="flex justify-center p-6">
          <Spinner className="size-6" />
        </div>
      ) : null}
    </div>
  );
}

export const ListTable = forwardRef(ListTableInner) as <T>(props: ListTableProps<T> & { ref?: ForwardedRef<ListTableHandle> }) => ReactElement;

/** The insured column: a router link to the row's page, named by the insured, that doesn't also fire the row click. */
export function insuredColumn<T>({
  header,
  insured,
  text = insured,
  path,
  size,
}: {
  header: string;
  insured: (row: T) => string | undefined;
  text?: (row: T) => ReactNode;
  path: (row: T) => string;
  size: number;
}): ListColumn<T> {
  return {
    id: 'insured',
    header,
    size,
    accessorFn: (row) => insured(row) ?? '',
    cell: ({ row }) => (
      <Link to={path(row.original)} className="font-bold text-text-subdued hover:underline" onClick={(event) => event.stopPropagation()}>
        {text(row.original)}
      </Link>
    ),
  };
}
